package com.sddamico.swigmore.components.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.brand.Wordmark
import com.sddamico.swigmore.ds.core.Avatar
import com.sddamico.swigmore.ds.core.SyncBadge
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.ds.navigation.NavTab
import com.sddamico.swigmore.ds.navigation.NavTabs
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.Routes
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Text

private val AppTab.navTab get() = NavTab(label, path)

// Until the tabs fit on one line with the wordmark and the account, they take a full-width row under
// the wordmark and the avatar takes the top-right corner. The sync chip shows only beside the avatar on
// that two-row header: a phone has no room for it, and on one row a commissioner's six tabs need its space.
val NavSlotStyle = CssStyle {
  base { Modifier.css("order" to "1", "flex" to "1 1 100%") }
  Breakpoint.XL { Modifier.css("order" to "0", "flex" to "1 1 0", "min-width" to "560px") }
}

val AccountSlotStyle = CssStyle {
  base { Modifier.css("margin-left" to "auto") }
  Breakpoint.XL { Modifier.css("margin-left" to "0") }
}

val SyncChipStyle = CssStyle {
  base { Modifier.css("display" to "none") }
  Breakpoint.MD { Modifier.css("display" to "inline") }
  Breakpoint.XL { Modifier.css("display" to "none") }
}

/**
 * Who is signed in, for the avatar and its sign-out menu. [name] is the league member's name, absent
 * for a commissioner who doesn't own a team. [signingOut] disables Sign out while it runs.
 */
class HeaderAccount(val email: String, val name: String?, val signingOut: Boolean = false, val onSignOut: () -> Unit)

/**
 * The app bar: wordmark, tabs, Fantrax sync chip and the account avatar.
 * [showAdmin] hides the Commish tab from non-commissioners.
 */
@Composable
fun AppHeader(
  activeTab: AppTab?,
  modifier: Modifier = Modifier,
  syncStatus: String? = null,
  account: HeaderAccount? = null,
  showAdmin: Boolean = true,
) {
  val ctx = rememberPageContext()
  var menuOpen by remember { mutableStateOf(false) }
  Header(attrs = Modifier.css("border-bottom" to "1px solid ${SwColor.hairline}").then(modifier).toAttrs()) {
    Div(
      attrs = Modifier.css("max-width" to "1180px", "margin" to "0 auto", "padding" to "14px 20px", "display" to "flex",
        "flex-wrap" to "wrap", "align-items" to "center", "gap" to "14px 16px").toAttrs()
    ) {
      Wordmark(onClick = { ctx.router.navigateTo(AppTab.Poll.path) })
      NavTabs(
        AppTab.entries.filter { showAdmin || it != AppTab.Admin }.map { it.navTab },
        activeTab?.navTab,
        NavSlotStyle.toModifier(),
      )
      Div(attrs = AccountSlotStyle.toModifier().css("display" to "flex", "align-items" to "center", "gap" to "10px",
        "position" to "relative").toAttrs()) {
        if (syncStatus != null) SyncBadge(syncStatus, SyncChipStyle.toModifier())
        if (account == null) {
          if (ctx.route.path != Routes.SIGN_IN) Avatar("?", onClick = { ctx.router.navigateTo(Routes.SIGN_IN) })
        } else {
          Avatar(initialsOf(account), onClick = { menuOpen = !menuOpen })
          if (menuOpen) AccountMenu(account, onClose = { menuOpen = false })
        }
      }
    }
  }
}

@Composable
private fun AccountMenu(account: HeaderAccount, onClose: () -> Unit) {
  DisposableEffect(Unit) {
    val onKey = { e: Event -> if ((e as KeyboardEvent).key == "Escape") onClose() }
    document.addEventListener("keydown", onKey)
    onDispose { document.removeEventListener("keydown", onKey) }
  }
  // Catches clicks anywhere outside the menu.
  Div(attrs = Modifier.css("position" to "fixed", "inset" to "0", "z-index" to "9").toAttrs { onClick { onClose() } })
  Div(
    attrs = Modifier.css("position" to "absolute", "top" to "calc(100% + 8px)", "right" to "0", "z-index" to "10",
      "background" to SwColor.field900, "border" to "1px solid ${SwColor.borderOnField}",
      "border-radius" to SwEffects.radiusLg, "padding" to "12px", "display" to "flex", "flex-direction" to "column",
      "gap" to "10px", "min-width" to "200px").toAttrs()
  ) {
    Div(attrs = Modifier.typeMono("11px").css("color" to SwColor.textMuted, "overflow-wrap" to "anywhere").toAttrs()) {
      Text(account.email)
    }
    Button("Sign out", variant = ButtonVariant.Outline, style = ButtonStyle.Caps, onDark = true, fullWidth = true,
      disabled = account.signingOut, onClick = account.onSignOut)
  }
}

/** Letters for the avatar, which keeps two: the name's word initials (or its one word), else the email's. */
private fun initialsOf(account: HeaderAccount): String {
  val words = account.name?.split(' ')?.map { it.filter(Char::isLetter) }?.filter { it.isNotEmpty() }.orEmpty()
  val letters = when (words.size) {
    0 -> account.email.substringBefore('@').filter(Char::isLetter)
    1 -> words.single()
    else -> words.joinToString("") { it.take(1) }
  }
  return letters.ifEmpty { "?" }
}
